import React, { useState } from 'react';
import { format } from 'date-fns';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import Dialog from './Dialog';
import Button from './Button';
import Form from './Formik/Form';
import AddressFieldEdit from './Formik/components/AddressFieldEdit';

import { countries } from '@dashboard/services/countries/countries';
import { getAddressData } from '@dashboard/services/getAddressData';
import { getDataFieldTypeLabel } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { getOrderType } from '@dashboard/services/getProductType';
import {
  fieldLabelText,
  MULTI_SELECT_ELEMENT,
  multiSelectOptionValue,
  splitMultiSelectValue,
} from '@repo/lib';

export default function FieldEditDialog({
  fields,
  page,
  updateFields,
  trigger,
  open: openProp,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = openProp ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const orderType = getOrderType(page);

  return (
    <Dialog
      trigger={
        trigger === null
          ? null
          : trigger || (
              <Button
                appearance="light"
                size="small"
                className="ml-2 flex items-center gap-x-2"
                onClick={() => setIsOpen(true)}
              >
                <PencilSquareIcon className="w-4 h-4" /> Edit
              </Button>
            )
      }
      title="Edit fields"
      description={`These fields only update ${orderType} details. To update a customer's saved name, email, or address, go to the customer's page.`}
      open={isOpen}
      onOpenChange={setIsOpen}
      onCancel={() => setIsOpen(false)}
      confirmButton={null}
      cancelButton={null}
    >
      {({ close }) => {
        const formFields = getEditFields(fields, page);

        return (
          <Form
            fields={formFields}
            initialValues={Object.fromEntries(formFields.map(field => [field.name, field._value]))}
            onSubmit={async (values, actions) => {
              try {
                const data = await updateFields(values);

                actions.setStatus('');
                actions.resetForm({
                  values: Object.fromEntries(
                    data.fields.map(field => [
                      field._id,
                      field.element === 'address'
                        ? getAddressData(field)
                        : field.element === MULTI_SELECT_ELEMENT
                          ? (field.values ?? [])
                          : field.value,
                    ]),
                  ),
                });
                setIsOpen(false);

                return {
                  data: true,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            cancelButton={{
              label: 'Cancel',
              appearance: 'light',
              onClick: close,
            }}
            formSubmitAlign="end"
          />
        );
      }}
    </Dialog>
  );
}

function getAddressFieldDescription(pageField, addressData) {
  const collect = pageField?.collect;
  if (collect === 'billing_and_shipping') return 'Billing & shipping address';
  if (collect === 'billing') return 'Billing address';

  const hasBilling = !!addressData?.billing;
  const hasShipping = !!addressData?.shipping;
  if (hasBilling && hasShipping) return 'Billing & shipping address';
  if (hasShipping) return 'Shipping address';
  return 'Billing address';
}

/**
 * Get fields for the edit dialog
 * @param {Object} payment
 * @returns {Array}
 *  @property {string} type - The type of the field
 *  @property {string} name - The name of the field
 *  @property {Array} options - The options of the field (checkbox, select, multiple-choice, country)
 *  @property {string} label - The label of the field
 *  @property {string} _value - The value of the field from the backend, prefixed with underscore to keep field uncontrolled
 */
function getEditFields(fields = [], page) {
  return fields.map(field => {
    const pageField = page?.fields?.find(f => f._id === field.fieldId);

    const fieldType = getDataFieldTypeLabel(field.type);

    if (pageField) {
      // The API rebuilds the answer by the element the row was stored with, which differs from
      // the field's own once a merchant changes Selections allowed after the purchase.
      switch (field.element || pageField.element) {
        case 'address':
          return {
            type: 'custom',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: getAddressFieldDescription(pageField, getAddressData(field)),
            pageField,
            _value: getAddressData(field) ?? {},
            renderField: (renderArgs, { setFieldValue, values }) => (
              <AddressFieldEdit field={renderArgs} values={values} setFieldValue={setFieldValue} />
            ),
          };
        case 'checkbox':
          return {
            type: 'checkbox',
            options: pageField.options,
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'textarea':
          return {
            type: 'textarea',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'select':
          return {
            type: 'select',
            options: pageField.options,
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'multi-select': {
          const options = pageField.options ?? [];
          const hasList = Array.isArray(field.values);
          const stored = hasList ? field.values : splitMultiSelectValue(field.value, options);
          // Index-aligned with `values` only, so a row without the list has no usable labels.
          const storedLabels = hasList ? (field.meta?.selectedOptionLabels ?? []) : [];
          const currentLabel = value =>
            options.find(option => multiSelectOptionValue(option) === value)?.label;
          // A stored answer is a snapshot: a value renamed or deleted since the purchase
          // still shows ticked under the label it was bought with, so saving keeps it.
          const storedOptions = stored.map((value, index) => ({
            name: storedLabels[index] || currentLabel(value) || value,
            value,
          }));
          const otherOptions = options
            .map(option => ({ name: option.label, value: multiSelectOptionValue(option) }))
            .filter(option => !stored.includes(option.value));
          return {
            type: 'listbox',
            multiple: true,
            summary: true,
            placeholder: 'Select options',
            options: [...storedOptions, ...otherOptions],
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: stored,
          };
        }
        case 'multiple-choice': {
          // A Multiple choice answer is its label (the options carry no value), and a
          // stored answer is a snapshot: a label whose option has since been renamed or
          // deleted still shows selected here, so saving keeps it.
          const labels = (pageField.options ?? []).map(option => option.label);
          const stored = typeof field.value === 'string' && field.value ? [field.value] : [];
          const stale = stored.filter(label => !labels.includes(label));
          return {
            type: 'select',
            options: [...labels, ...stale].map(label => ({ label, value: label })),
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        }
        case 'quantity':
          return {
            type: 'number',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'phone':
          return {
            type: 'tel',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'country':
          return {
            type: 'select',
            options: [
              {
                label: 'Select country',
                value: '',
              },
              ...countries.map(country => ({
                label: country.name,
                value: country.alpha2,
              })),
            ],
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'tax-id':
          return {
            type: 'text',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'email':
          return {
            type: 'email',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'time':
          return {
            type: 'time',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        case 'date-time':
          return {
            type: 'datetime-local',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value ? format(new Date(field.value), 'yyyy-MM-dd HH:mm') : '',
          };
        case 'date':
          return {
            type: 'date',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value ? format(new Date(field.value), 'yyyy-MM-dd') : '',
          };
        case 'po-number':
          return {
            type: 'text',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
        default:
          return {
            type: 'text',
            name: field._id,
            label: fieldLabelText(field.label),
            labelDescription: fieldType,
            _value: field.value,
          };
      }
    }

    return {
      type: 'text',
      name: field._id,
      label: fieldLabelText(field.label),
      labelDescription: fieldType,
    };
  });
}
